import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

export interface ShiftTimeOption {
    startTime: string | null;
    endTime: string | null;
    startTimeFormatted: string | null;
    endTimeFormatted: string | null;
}

interface ShiftTimeState {
    options: ShiftTimeOption[];
    loading: boolean;
    error: string | null;
}

const initialState: ShiftTimeState = {
    options: [],
    loading: false,
    error: null,
};


// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch distinct shift time options (start/end pairs) from the shift schedule.
 * Uses: GET /api/shift/shiftTimeOptions/{siteCd}
 */
export const fetchShiftTimeOptions = createAsyncThunk(
    'ShiftTime/fetchShiftTimeOptions',
    async (siteCd: string, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(`${ENDPOINTS.GET_SHIFT_TIME_OPTIONS}/${siteCd}`);
            return response as ShiftTimeOption[];
        } catch (error: any) {
            console.error('❌ ShiftTimeOptions API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch shift time options');
        }
    }
);


// ========================================
// SLICE
// ========================================
const shiftTimeSlice = createSlice({
    name: 'shiftTime',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShiftTimeOptions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShiftTimeOptions.fulfilled, (state, action) => {
                state.loading = false;
                state.options = action.payload || [];
            })
            .addCase(fetchShiftTimeOptions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = shiftTimeSlice.actions;
export default shiftTimeSlice.reducer;